import React, { memo, useMemo, useEffect, useState, useCallback } from 'react';
import {
  GridColumnVisibilityModel,
  GridPagination,
  GridRowCount,
  GridSelectedRowCount,
  gridFilteredSortedRowIdsSelector,
} from '@mui/x-data-grid-pro';
import { FlexBox } from 'components/FlexBox';
import { GridViewModal } from './gridView/GridViewModal';
import {
  ComponentGridViewModel,
  GridViewState,
  defaultComponentGridViewModel,
  defaultFilterOptions,
  ensureFilterItemsHaveIds,
} from '../../data/GridViewData';
import { isEqual } from 'lodash';

interface CustomFooterProps {
  pagination?: boolean;
  rowCount: number;
  componentName?: string;
  transitionEnumId?: string;
  configuredTransitionId?: string;
  researchProjectId?: string;
  apiRef?: any;
  setHasCustomViewApplied?: (hasApplied: boolean) => void;
  setColumnVisibilityModel?: (model: GridColumnVisibilityModel) => void;
  onApplyViewColumns?: (fields: string[]) => void;
  footerActions?: React.ReactNode;
}

/**
 * Custom footer for DataGrid with pagination/row count and view management.
 * When componentName is provided, renders GridViewModal for selecting and creating filters/layouts.
 */
export const GridCustomFooter = memo(
  ({
    pagination = false,
    rowCount = 0,
    componentName,
    transitionEnumId,
    configuredTransitionId,
    researchProjectId,
    apiRef,
    setHasCustomViewApplied,
    setColumnVisibilityModel,
    onApplyViewColumns,
    footerActions,
  }: CustomFooterProps) => {
    const [currentViewModel, setCurrentViewModel] = useState<ComponentGridViewModel>(defaultComponentGridViewModel);
    const [filteredRowCount, setFilteredRowCount] = useState<number>(rowCount);
    const [selectedRowCount, setSelectedRowCount] = useState<number>(0);
    const api = apiRef?.current;

    const getCurrentGridState = useCallback((): GridViewState => {
      if (!api || !componentName)
        return {
          sortOptions: [],
          columnVisibility: {},
          filterOptions: defaultFilterOptions,
          columnOrder: [],
        };
      return {
        sortOptions: api.getSortModel(),
        columnVisibility: Object.fromEntries(
          Object.entries(api.state.columns.columnVisibilityModel)
            .filter(([_, value]) => value === true)
            .map(([key, value]) => [key, value as boolean])
        ),
        filterOptions: api.state.filter.filterModel || defaultFilterOptions,
        columnOrder: api.getVisibleColumns().map((col: any) => col.field),
      };
    }, [api, componentName]);

    const updateColumnVisibility = useCallback(
      (columnsToShow: string[]) => {
        if (!api) return;

        const currentVisibility = api.state.columns.columnVisibilityModel;
        const newVisibility: GridColumnVisibilityModel = { ...currentVisibility };

        const validColumns = columnsToShow.filter(field => api.getColumn(field));
        validColumns.forEach(field => (newVisibility[field] = true));
        if (api.getAllColumns().some((col: any) => col.field === '__check__')) {
          newVisibility['__check__'] = true;
        }

        // Hide other columns
        Object.keys(newVisibility).forEach(field => {
          if (!validColumns.includes(field) && field !== '__check__') {
            newVisibility[field] = false;
          }
        });

        if (!isEqual(currentVisibility, newVisibility)) {
          setHasCustomViewApplied?.(true);
          const allVisibleColumns = Object.entries(newVisibility)
            .filter(([_, isVisible]) => isVisible)
            .map(([field, _]) => field);
          onApplyViewColumns?.(allVisibleColumns);
          setColumnVisibilityModel
            ? setColumnVisibilityModel(newVisibility)
            : api.setColumnVisibilityModel(newVisibility);
        }
      },
      [api, setHasCustomViewApplied, onApplyViewColumns, setColumnVisibilityModel]
    );

    // Check for changes in the view model
    const viewModelChanges = useMemo(() => {
      if (!currentViewModel || !componentName) return null;

      const currentGridState = getCurrentGridState();

      const hasCustomVisibilityFields =
        currentViewModel.columnVisibility && Object.keys(currentViewModel.columnVisibility).length > 0;

      const changes = {
        sortChanged:
          currentViewModel.sortOptions && !isEqual(currentGridState.sortOptions, currentViewModel.sortOptions),
        visibilityChanged:
          hasCustomVisibilityFields && !isEqual(currentGridState.columnVisibility, currentViewModel.columnVisibility),
        filterChanged:
          currentViewModel.filterOptions &&
          !isEqual(currentGridState.filterOptions, ensureFilterItemsHaveIds(currentViewModel.filterOptions)),
        orderChanged:
          currentViewModel.columnOrder && !isEqual(currentGridState.columnOrder, currentViewModel.columnOrder),
      };

      return Object.values(changes).some(Boolean) ? changes : null;
    }, [currentViewModel, componentName, getCurrentGridState]);

    // Apply changes to the Grid when the view model changes
    useEffect(() => {
      if (!viewModelChanges || !api || !componentName) return;

      if (viewModelChanges.visibilityChanged || viewModelChanges.filterChanged) {
        const layoutColumns = Object.keys(currentViewModel.columnVisibility || {});
        const filterColumns = currentViewModel.filterOptions?.items?.map(item => item.field) || [];

        const allColumns = [...layoutColumns, ...filterColumns];
        if (allColumns.length > 0) {
          updateColumnVisibility(allColumns);
        }
      }

      if (viewModelChanges.filterChanged) {
        const processedFilterModel = ensureFilterItemsHaveIds(currentViewModel.filterOptions);
        api.setFilterModel(processedFilterModel);
      }

      if (viewModelChanges.sortChanged) {
        api.setSortModel(currentViewModel.sortOptions);
      }

      if (viewModelChanges.orderChanged) {
        const currentColumns = api.getAllColumns().map((col: any) => col.field);
        const validOrder = currentViewModel.columnOrder.filter(field => currentColumns.includes(field));

        validOrder.forEach((field: string, index: number) => {
          const column = api.getColumn(field);
          if (column) {
            api.setColumnIndex(field, index);
          }
        });
      }
    }, [viewModelChanges, currentViewModel, api, componentName, updateColumnVisibility]);

    // Update filtered row count when grid state changes
    useEffect(() => {
      if (!api || !api.state) {
        setFilteredRowCount(rowCount);
        return;
      }

      const updateFilteredCount = () => {
        const filterItems = api.state?.filter?.filterModel?.items || [];

        if (filterItems.length === 0) {
          setFilteredRowCount(rowCount);
        } else {
          const filteredRows = gridFilteredSortedRowIdsSelector(api.state);
          setFilteredRowCount(Array.isArray(filteredRows) ? filteredRows.length : rowCount);
        }
      };

      updateFilteredCount();

      const unsubscribe = api.subscribeEvent('filterModelChange', () => {
        setTimeout(updateFilteredCount, 0);
      });

      return unsubscribe;
    }, [api, rowCount]);

    // Update selected row count when selection changes
    useEffect(() => {
      if (!api || !api.state) {
        setSelectedRowCount(0);
        return;
      }
      const updateSelectedCount = () => {
        const selectedRows = api.getSelectedRows ? api.getSelectedRows() : new Map();
        setSelectedRowCount(selectedRows.size);
      };
      updateSelectedCount();
      const unsubscribe = api.subscribeEvent('rowSelectionChange', () => {
        setTimeout(updateSelectedCount, 0);
      });
      return unsubscribe;
    }, [api]);

    const handleViewModelChange = useCallback((viewModel: ComponentGridViewModel) => {
      setCurrentViewModel(viewModel);
    }, []);

    return (
      <FlexBox
        flexDirection={'row'}
        justifyContent='space-between'
        width='100%'
        sx={{ borderTop: '1px solid rgba(224, 224, 224, 1)' }}
      >
        <FlexBox flexDirection='row'>
          {componentName ? (
            <GridViewModal
              componentName={componentName}
              transitionEnumId={transitionEnumId}
              configuredTransitionId={configuredTransitionId}
              researchProjectId={researchProjectId}
              getCurrentGridState={getCurrentGridState}
              currentViewModel={currentViewModel}
              onApplyView={handleViewModelChange}
            />
          ) : (
            <span />
          )}
          {footerActions}
        </FlexBox>
        {selectedRowCount > 0 && <GridSelectedRowCount selectedRowCount={selectedRowCount} />}
        {pagination ? <GridPagination /> : <GridRowCount rowCount={rowCount} visibleRowCount={filteredRowCount} />}
      </FlexBox>
    );
  }
);

GridCustomFooter.displayName = 'GridCustomFooter';
